/**
 * Admin Access Page
 * ==================
 * Receives admin-generated JWT token and sets cookie for dashboard access
 *
 * Flow:
 * 1. Admin clicks "Manage Dashboard" in Sales Portal
 * 2. Backend generates temporary JWT token
 * 3. This page receives token via URL parameter
 * 4. Sets auth_token cookie
 * 5. Redirects to restaurant dashboard
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';

const AdminAccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('No access token provided');
      return;
    }

    try {
      // Set the auth token cookie (same as OAuth callback)
      Cookies.set('auth_token', token, {
        expires: 1 / 24, // 1 hour (shorter expiry for admin access)
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
        path: '/'
      });

      // Clear any demo session flags
      localStorage.removeItem('isDemo');

      setStatus('success');

      // Redirect to dashboard after brief delay
      setTimeout(() => {
        navigate('/restaurant-dashboard');
      }, 1500);

    } catch (err: any) {
      console.error('Failed to set admin access token:', err);
      setStatus('error');
      setErrorMessage('Failed to configure dashboard access');
    }
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-[#1f4ed1] animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Configuring admin access...</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-6"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Failed</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => window.close()}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close Window
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md mx-auto p-6"
      >
        <ShieldCheck className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Access Granted</h2>
        <p className="text-gray-600 mb-4">
          Redirecting to restaurant dashboard...
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> You are accessing this dashboard as an admin support user.
            Token expires in 1 hour.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminAccess;
